import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function superadminAxios() {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: `${API_URL}/superadmin`,
    headers: { Authorization: `Bearer ${token}` }
  });
}

function StatCard({ label, value, color }) {
  return (
    <div style={{
      flex: '1', minWidth: '140px', padding: '16px 20px',
      background: 'var(--bg-secondary)', borderRadius: '10px',
      borderLeft: `3px solid ${color}`
    }}>
      <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '28px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color }}>{value}</div>
    </div>
  );
}

function SuperAdmin() {
  const [stats, setStats] = useState(null);
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHousehold, setSelectedHousehold] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(null);
  const [userSuspendReason, setUserSuspendReason] = useState('');
  const [showUserSuspendForm, setShowUserSuspendForm] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [actionMsg, setActionMsg] = useState('');
  const [activeTab, setActiveTab] = useState('households');
  const [pendingRegs, setPendingRegs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [denyForm, setDenyForm] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityHousehold, setActivityHousehold] = useState('');

  const flash = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 3000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const api = superadminAxios();
      const [statsRes, householdsRes] = await Promise.all([
        api.get('/stats'),
        api.get('/households')
      ]);
      setStats(statsRes.data);
      setHouseholds(householdsRes.data);
    } catch (error) {
      console.error('SuperAdmin fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const api = superadminAxios();
      const res = await api.get('/pending-registrations');
      setPendingRegs(res.data);
    } catch (error) {
      console.error('Pending registrations fetch error:', error);
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const handleApprove = async (reg) => {
    try {
      const api = superadminAxios();
      await api.post(`/pending-registrations/${reg.id}/approve`);
      flash(`${reg.username} approved — account created.`);
      fetchPending();
      fetchAll();
    } catch (error) {
      flash(error.response?.data?.error || 'Approval failed.');
    }
  };

  const handleDeny = async (reg) => {
    try {
      const api = superadminAxios();
      await api.post(`/pending-registrations/${reg.id}/deny`, { reason: denyReason });
      flash(`${reg.username}'s request denied.`);
      setDenyForm(null);
      setDenyReason('');
      fetchPending();
    } catch (error) {
      flash(error.response?.data?.error || 'Denial failed.');
    }
  };

  const fetchActivity = useCallback(async (householdId) => {
    setActivityLoading(true);
    try {
      const api = superadminAxios();
      const params = householdId ? `?household_id=${householdId}` : '';
      const res = await api.get(`/activity-log${params}`);
      setActivityLog(res.data);
    } catch (error) {
      console.error('Activity log fetch error:', error);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { if (activeTab === 'pending') fetchPending(); }, [activeTab, fetchPending]);
  useEffect(() => { if (activeTab === 'activity') fetchActivity(activityHousehold); }, [activeTab, fetchActivity, activityHousehold]);

  const openHousehold = async (household) => {
    setSelectedHousehold(household);
    setMembersLoading(true);
    try {
      const api = superadminAxios();
      const res = await api.get(`/households/${household.id}/members`);
      setMembers(res.data);
    } catch (error) {
      console.error('Fetch members error:', error);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleSuspend = async (householdId) => {
    try {
      const api = superadminAxios();
      await api.patch(`/households/${householdId}/suspend`, { reason: suspendReason });
      flash('Household suspended.');
      setShowSuspendForm(null);
      setSuspendReason('');
      await fetchAll();
      if (selectedHousehold?.id === householdId) {
        setSelectedHousehold(h => ({ ...h, is_suspended: true, suspended_reason: suspendReason }));
      }
    } catch (error) {
      console.error('Suspend error:', error);
    }
  };

  const handleUnsuspend = async (householdId) => {
    try {
      const api = superadminAxios();
      await api.patch(`/households/${householdId}/unsuspend`);
      flash('Household reinstated.');
      await fetchAll();
      if (selectedHousehold?.id === householdId) {
        setSelectedHousehold(h => ({ ...h, is_suspended: false, suspended_reason: null }));
      }
    } catch (error) {
      console.error('Unsuspend error:', error);
    }
  };

  const handleDeleteHousehold = async (household) => {
    if (!window.confirm(`Permanently delete "${household.name}" and all its data? This cannot be undone.`)) return;
    try {
      const api = superadminAxios();
      await api.delete(`/households/${household.id}`);
      flash('Household deleted.');
      setSelectedHousehold(null);
      await fetchAll();
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleResetPassword = async (member) => {
    if (!window.confirm(`Reset password for ${member.full_name || member.username}? A temporary password will be sent to ${member.email}.`)) return;
    try {
      const api = superadminAxios();
      await api.post(`/users/${member.id}/reset-password`);
      flash(`Temporary password emailed to ${member.email}.`);
    } catch (error) {
      flash(error.response?.data?.error || 'Failed to reset password.');
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Remove ${user.full_name || user.username} from their household?`)) return;
    try {
      const api = superadminAxios();
      await api.patch(`/users/${user.id}/remove`);
      flash(`${user.full_name || user.username} removed.`);
      setMembers(m => m.filter(u => u.id !== user.id));
      await fetchAll();
    } catch (error) {
      console.error('Remove user error:', error);
    }
  };

  const handleSuspendUser = async (member, reason) => {
    try {
      const api = superadminAxios();
      await api.patch(`/users/${member.id}/suspend`, { reason });
      flash(`${member.full_name || member.username} suspended.`);
      setMembers(m => m.map(u => u.id === member.id
        ? { ...u, is_suspended: true, suspended_reason: reason }
        : u
      ));
    } catch (error) {
      console.error('Suspend user error:', error);
    }
  };

  const handleUnsuspendUser = async (member) => {
    try {
      const api = superadminAxios();
      await api.patch(`/users/${member.id}/unsuspend`);
      flash(`${member.full_name || member.username} reinstated.`);
      setMembers(m => m.map(u => u.id === member.id
        ? { ...u, is_suspended: false, suspended_reason: null }
        : u
      ));
    } catch (error) {
      console.error('Unsuspend user error:', error);
    }
  };

  const filteredHouseholds = households.filter(h => {
    if (filter === 'active' && h.is_suspended) return false;
    if (filter === 'suspended' && !h.is_suspended) return false;
    if (search && !h.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Super Admin</h2>
          <p>Manage all Family Nests across the platform</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              const email = window.prompt('Send invite email to:');
              if (!email) return;
              const name = window.prompt('Their name (optional):') || '';
              try {
                const api = superadminAxios();
                await api.post('/send-invite', { email, name });
                flash(`Invite sent to ${email}`);
              } catch {
                flash('Failed to send invite email.');
              }
            }}
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '13px' }}>
            Send Invite Email
          </button>
          <button
            onClick={async () => {
              const email = window.prompt('Send test approval email to:', 'aaron.prestenbach@gmail.com');
              if (!email) return;
              try {
                const api = superadminAxios();
                await api.post('/test-approval-email', { email });
                flash(`Test approval email sent to ${email}`);
              } catch {
                flash('Failed to send test email.');
              }
            }}
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '13px' }}>
            Send Test Approval Email
          </button>
        </div>
      </div>

      {actionMsg && (
        <div style={{
          marginBottom: '16px', padding: '12px 16px', borderRadius: '8px',
          background: 'rgba(14,188,212,0.12)', color: 'var(--accent-coral)',
          fontWeight: 600, fontSize: '14px'
        }}>
          {actionMsg}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
          <StatCard label="Total Households" value={stats.total_households} color="var(--accent-coral)" />
          <StatCard label="Active" value={stats.active_households} color="#22c55e" />
          <StatCard label="Suspended" value={stats.suspended_households} color="#ef4444" />
          <StatCard label="Total Users" value={stats.total_users} color="var(--accent-amber)" />
          <StatCard label="Pending Requests" value={stats.pending_requests ?? 0} color="#a78bfa" />
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--border-color)', paddingBottom: '0' }}>
        {[
          { id: 'households', label: 'Family Nests' },
          { id: 'pending', label: `Access Requests${stats?.pending_requests > 0 ? ` (${stats.pending_requests})` : ''}` },
          { id: 'activity', label: 'Activity Log' }
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 18px', fontSize: '14px', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === tab.id ? '2px solid var(--accent-coral)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--accent-coral)' : 'var(--text-muted)',
              marginBottom: '-1px'
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pending Registrations Panel */}
      {activeTab === 'pending' && (
        <div>
          {pendingLoading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : pendingRegs.length === 0 ? (
            <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <p style={{ color: 'var(--text-muted)' }}>No access requests</p>
            </div>
          ) : (
            <div className="card card-table">
              <div className="tracker-table-wrap">
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th className="tracker-th">Name</th>
                      <th className="tracker-th">Username</th>
                      <th className="tracker-th">Email</th>
                      <th className="tracker-th">Requested</th>
                      <th className="tracker-th">Status</th>
                      <th className="tracker-th"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRegs.map(reg => (
                      <React.Fragment key={reg.id}>
                        <tr className="tracker-row">
                          <td className="tracker-td tracker-name">{reg.full_name || '—'}</td>
                          <td className="tracker-td" style={{ fontFamily: 'monospace', fontSize: '13px' }}>{reg.username}</td>
                          <td className="tracker-td" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{reg.email}</td>
                          <td className="tracker-td" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {new Date(reg.requested_at).toLocaleDateString()}
                          </td>
                          <td className="tracker-td">
                            <span style={{
                              fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px',
                              background: reg.status === 'pending' ? 'rgba(167,139,250,0.15)'
                                : reg.status === 'approved' ? 'rgba(34,197,94,0.12)'
                                : 'rgba(239,68,68,0.12)',
                              color: reg.status === 'pending' ? '#a78bfa'
                                : reg.status === 'approved' ? '#22c55e'
                                : '#ef4444'
                            }}>
                              {reg.status.charAt(0).toUpperCase() + reg.status.slice(1)}
                            </span>
                          </td>
                          <td className="tracker-td">
                            {reg.status === 'pending' && (
                              <div className="list-item-actions">
                                <button onClick={() => handleApprove(reg)}
                                  className="btn btn-success btn-sm">
                                  Approve
                                </button>
                                <button onClick={() => { setDenyForm(reg.id); setDenyReason(''); }}
                                  className="btn btn-sm"
                                  style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                                  Deny
                                </button>
                              </div>
                            )}
                            {reg.status === 'denied' && reg.deny_reason && (
                              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{reg.deny_reason}</span>
                            )}
                          </td>
                        </tr>
                        {denyForm === reg.id && (
                          <tr>
                            <td colSpan={6} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.05)' }}>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  placeholder="Reason for denial (optional, sent to applicant)"
                                  value={denyReason}
                                  onChange={e => setDenyReason(e.target.value)}
                                  style={{ flex: 1, minWidth: '200px', fontSize: '13px' }}
                                  autoFocus
                                />
                                <button onClick={() => handleDeny(reg)} className="btn btn-sm"
                                  style={{ background: '#ef4444', color: '#fff', border: 'none' }}>
                                  Confirm Deny
                                </button>
                                <button onClick={() => setDenyForm(null)} className="btn btn-secondary btn-sm">
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'activity' && (
        <div>
          <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              className="form-input"
              value={activityHousehold}
              onChange={e => setActivityHousehold(e.target.value)}
              style={{ minWidth: '200px', fontSize: '13px', padding: '6px 10px' }}>
              <option value="">All Family Nests</option>
              {households.map(h => (
                <option key={h.id} value={h.id}>{h.name}</option>
              ))}
            </select>
            <button onClick={() => fetchActivity(activityHousehold)} className="btn btn-secondary btn-sm">
              Refresh
            </button>
          </div>

          {activityLoading ? (
            <div className="loading"><div className="spinner"></div></div>
          ) : activityLog.length === 0 ? (
            <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
              <p style={{ color: 'var(--text-muted)' }}>No activity recorded yet</p>
            </div>
          ) : (
            <div className="card card-table">
              <div className="tracker-table-wrap">
                <table className="tracker-table">
                  <thead>
                    <tr>
                      <th className="tracker-th">Time</th>
                      <th className="tracker-th">User</th>
                      <th className="tracker-th">Family Nest</th>
                      <th className="tracker-th">Action</th>
                      <th className="tracker-th">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLog.map(entry => {
                      const actionColors = {
                        'login': '#a78bfa',
                        'recipe:create': '#22c55e',
                        'grocery:add': '#f59e0b',
                        'todo:create': '#0ea5e9',
                        'bill:create': '#ef4444',
                        'subscription:create': '#ec4899',
                        'debt:create': '#f97316',
                        'weblink:create': '#14b8a6',
                      };
                      const actionLabels = {
                        'login': 'Login',
                        'recipe:create': 'Recipe Created',
                        'grocery:add': 'Grocery Added',
                        'todo:create': 'Task Created',
                        'bill:create': 'Bill Created',
                        'subscription:create': 'Subscription Added',
                        'debt:create': 'Debt Added',
                        'weblink:create': 'Web Link Added',
                      };
                      const color = actionColors[entry.action] || 'var(--text-muted)';
                      return (
                        <tr key={entry.id} className="tracker-row">
                          <td className="tracker-td" style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                            {new Date(entry.created_at).toLocaleString()}
                          </td>
                          <td className="tracker-td" style={{ fontFamily: 'monospace', fontSize: '13px' }}>
                            {entry.username || '—'}
                            {entry.full_name && entry.full_name !== entry.username && (
                              <span style={{ color: 'var(--text-muted)', marginLeft: '6px', fontSize: '12px' }}>({entry.full_name})</span>
                            )}
                          </td>
                          <td className="tracker-td" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                            {entry.household_name || '—'}
                          </td>
                          <td className="tracker-td">
                            <span style={{
                              fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px',
                              background: `${color}22`, color
                            }}>
                              {actionLabels[entry.action] || entry.action}
                            </span>
                          </td>
                          <td className="tracker-td" style={{ fontSize: '13px', color: 'var(--text-secondary)', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.detail || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'households' && (
      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Household list panel */}
        <div style={{ flex: '1', minWidth: '280px' }}>
          <div className="card card-table">
            {/* Filter bar */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search Family Nests..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: '140px', padding: '6px 10px', fontSize: '13px' }}
              />
              {['all', 'active', 'suspended'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ textTransform: 'capitalize' }}>
                  {f}
                </button>
              ))}
            </div>

            {filteredHouseholds.length === 0 ? (
              <div className="empty-state" style={{ padding: '32px' }}>
                <div className="empty-state-icon">🏠</div>
                <p className="empty-state-text">No households found</p>
              </div>
            ) : (
              <div>
                {filteredHouseholds.map(h => (
                  <div key={h.id}
                    onClick={() => openHousehold(h)}
                    style={{
                      padding: '14px 16px', cursor: 'pointer',
                      borderBottom: '1px solid var(--border-color)',
                      background: selectedHousehold?.id === h.id ? 'var(--bg-tertiary, rgba(14,188,212,0.06))' : 'transparent',
                      borderLeft: selectedHousehold?.id === h.id ? '3px solid var(--accent-coral)' : '3px solid transparent',
                      transition: 'background 0.15s'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
                          {h.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                          {h.member_count} member{h.member_count !== 1 ? 's' : ''} · joined {new Date(h.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <span style={{
                        fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px',
                        background: h.is_suspended ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.12)',
                        color: h.is_suspended ? '#ef4444' : '#22c55e'
                      }}>
                        {h.is_suspended ? 'Suspended' : 'Active'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Household detail panel */}
        {selectedHousehold && (
          <div style={{ flex: '2', minWidth: '320px' }}>
            <div className="card">
              <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{selectedHousehold.name}</h3>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                      Invite code: <span style={{ fontFamily: 'monospace', color: 'var(--text-secondary)' }}>{selectedHousehold.invite_code}</span>
                    </div>
                    {selectedHousehold.is_suspended && selectedHousehold.suspended_reason && (
                      <div style={{
                        marginTop: '10px', padding: '8px 12px', borderRadius: '6px',
                        background: 'rgba(239,68,68,0.1)', color: '#ef4444', fontSize: '13px'
                      }}>
                        <strong>Suspension reason:</strong> {selectedHousehold.suspended_reason}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {selectedHousehold.is_suspended ? (
                      <button onClick={() => handleUnsuspend(selectedHousehold.id)} className="btn btn-success btn-sm">
                        Reinstate
                      </button>
                    ) : (
                      <button onClick={() => setShowSuspendForm(selectedHousehold.id)} className="btn btn-warning btn-sm"
                        style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                        Suspend
                      </button>
                    )}
                    <button onClick={() => handleDeleteHousehold(selectedHousehold)} className="btn btn-danger btn-sm">
                      Delete
                    </button>
                  </div>
                </div>

                {showSuspendForm === selectedHousehold.id && (
                  <div style={{ marginTop: '14px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Reason for suspension (shown to users)"
                      value={suspendReason}
                      onChange={e => setSuspendReason(e.target.value)}
                      style={{ flex: 1, minWidth: '200px', fontSize: '13px' }}
                    />
                    <button onClick={() => handleSuspend(selectedHousehold.id)} className="btn btn-sm"
                      style={{ background: '#ef4444', color: '#fff', border: 'none' }}>
                      Confirm Suspend
                    </button>
                    <button onClick={() => setShowSuspendForm(null)} className="btn btn-secondary btn-sm">Cancel</button>
                  </div>
                )}
              </div>

              {/* Members table */}
              <div style={{ padding: '16px 20px 8px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Members
              </div>
              {membersLoading ? (
                <div style={{ padding: '24px', textAlign: 'center' }}><div className="spinner"></div></div>
              ) : members.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>No members</div>
              ) : (
                <div className="tracker-table-wrap">
                  <table className="tracker-table">
                    <thead>
                      <tr>
                        <th className="tracker-th">Name</th>
                        <th className="tracker-th">Username</th>
                        <th className="tracker-th">Email</th>
                        <th className="tracker-th">Role</th>
                        <th className="tracker-th">Status</th>
                        <th className="tracker-th">Joined</th>
                        <th className="tracker-th"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map(member => (
                        <React.Fragment key={member.id}>
                          <tr className="tracker-row">
                            <td className="tracker-td tracker-name">{member.full_name || '—'}</td>
                            <td className="tracker-td" style={{ fontFamily: 'monospace', fontSize: '13px' }}>{member.username}</td>
                            <td className="tracker-td" style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{member.email}</td>
                            <td className="tracker-td">
                              <span className={`badge ${member.role === 'admin' ? 'badge-primary' : 'badge-secondary'}`}>
                                {member.role}
                              </span>
                            </td>
                            <td className="tracker-td">
                              <span style={{
                                fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px',
                                background: member.is_suspended ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.12)',
                                color: member.is_suspended ? '#ef4444' : '#22c55e'
                              }}>
                                {member.is_suspended ? 'Suspended' : 'Active'}
                              </span>
                            </td>
                            <td className="tracker-td" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                              {new Date(member.created_at).toLocaleDateString()}
                            </td>
                            <td className="tracker-td">
                              {!member.is_super_admin && (
                                <div className="list-item-actions">
                                  {member.is_suspended ? (
                                    <button onClick={() => handleUnsuspendUser(member)}
                                      className="btn btn-success btn-sm">
                                      Reinstate
                                    </button>
                                  ) : (
                                    <button onClick={() => {
                                      setShowUserSuspendForm(member.id);
                                      setUserSuspendReason('');
                                    }}
                                      className="btn btn-sm"
                                      style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                                      Suspend
                                    </button>
                                  )}
                                  <button onClick={() => handleResetPassword(member)}
                                    className="btn btn-sm"
                                    style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                                    Reset Password
                                  </button>
                                  <button onClick={() => handleRemoveUser(member)} className="btn btn-danger btn-sm">
                                    Remove
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {showUserSuspendForm === member.id && (
                            <tr>
                              <td colSpan={7} style={{ padding: '8px 16px', background: 'rgba(239,68,68,0.05)' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <input
                                    type="text"
                                    className="form-input"
                                    placeholder="Reason for suspension (shown to user on login)"
                                    value={userSuspendReason}
                                    onChange={e => setUserSuspendReason(e.target.value)}
                                    style={{ flex: 1, minWidth: '200px', fontSize: '13px' }}
                                    autoFocus
                                  />
                                  <button onClick={() => {
                                    handleSuspendUser(member, userSuspendReason);
                                    setShowUserSuspendForm(null);
                                  }}
                                    className="btn btn-sm"
                                    style={{ background: '#ef4444', color: '#fff', border: 'none' }}>
                                    Confirm
                                  </button>
                                  <button onClick={() => setShowUserSuspendForm(null)} className="btn btn-secondary btn-sm">
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {!selectedHousehold && (
          <div style={{ flex: '2', minWidth: '320px' }}>
            <div className="card" style={{ padding: '48px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏠</div>
              <p style={{ color: 'var(--text-muted)' }}>Select a household to view details</p>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default SuperAdmin;
